import { useState, useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import { p1113Api, F113, F1131, F1132, F1133 } from '../../../services/p1113Api';

export type Mode = 'browse' | 'add' | 'edit';

// ── Empty row factories ────────────────────────────────────────
export const emptyF1131 = (): F1131 => ({ f1131_01: '', f1131_02: '', f1131_03: '', f1131_04: '' });
export const emptyF1132 = (): F1132 => ({ f1132_01: '', f1132_02: '', f1132_03: 0, f1132_11: '' });
export const emptyF1133 = (): F1133 => ({ f1133_01: '', f1133_02: '', f1133_03: 0, f1133_11: '' });

// ── Default empty form data ─────────────────────────────────
const defaultFormData: Partial<F113> = {
  f113_01: '', f113_02: '', f113_11: '', f113_12: '', f113_18: '',
  f113_19: '', f113_20: '', f113_21: '', f113_22: 0, f113_24: 0,
  f113_241: 0, f113_26: 0, f113_27: '', f113_29: 0, f113_30: '',
  f113_31: false, f113_37: false, f113_38: 0, f113_39: false, f113_46: 0,
  f113_411: '', f113_412: '', f113_413: '', f113_414: '', f113_415: '',
  f113_416: 0, f113_417: 0, f113_418: 0, f113_419: 0, f113_421: '',
};

export function useProductForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [allIds, setAllIds] = useState<{ pk1: string; pk2: string }[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState<F113 | null>(null);
  const [formData, setFormData] = useState<Partial<F113>>({ ...defaultFormData });

  // Subtable state
  const [customerCodes, setCustomerCodes] = useState<F1131[]>([]);
  const [compositions, setCompositions] = useState<F1132[]>([]);
  const [specClasses, setSpecClasses] = useState<F1133[]>([]);

  const isEditing = mode !== 'browse';
  const loadedRef = useRef(false);

  // ── getField / setField ─────────────────────────────────────
  const getField = useCallback(
    (field: keyof F113): unknown => formData[field] ?? '',
    [formData]
  );

  const setField = useCallback((field: keyof F113, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // ── Load list (navigation IDs) ─────────────────────────────
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await p1113Api.list(1, 5000);
      const ids = (res.data || []).map(r => ({ pk1: r.f113_01, pk2: r.f113_02 }));
      setAllIds(ids);
      return ids;
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load single record ─────────────────────────────────────
  const loadRecord = useCallback(async (pk1: string, pk2: string) => {
    try {
      const d = await p1113Api.get(pk1, pk2);
      setRecord(d);
      setFormData({ ...d });
      setCustomerCodes(d.customer_codes || []);
      setCompositions(d.compositions || []);
      setSpecClasses(d.spec_classes || []);
    } catch {
      setRecord(null);
    }
  }, []);

  // ── Navigation ────────────────────────────────────────────
  const navTo = useCallback((idx: number) => {
    if (idx >= 0 && idx < allIds.length) {
      setCurrentIdx(idx);
    }
  }, [allIds.length]);

  const navFirst = useCallback(() => navTo(0), [navTo]);
  const navPrev = useCallback(() => navTo(currentIdx - 1), [navTo, currentIdx]);
  const navNext = useCallback(() => navTo(currentIdx + 1), [navTo, currentIdx]);
  const navLast = useCallback(() => navTo(allIds.length - 1), [navTo, allIds.length]);

  // Load record when navigation changes (browse mode)
  useEffect(() => {
    if (mode === 'browse' && currentIdx >= 0 && currentIdx < allIds.length) {
      const { pk1, pk2 } = allIds[currentIdx];
      loadRecord(pk1, pk2);
    }
  }, [currentIdx, mode, allIds, loadRecord]);

  // Initial load
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadList().then(ids => {
      if (ids.length > 0) setCurrentIdx(0);
    });
  }, [loadList]);

  // ── Start Add ──────────────────────────────────────────────
  const startAdd = useCallback(() => {
    setMode('add');
    setFormData({ ...defaultFormData });
    setCustomerCodes([]);
    setCompositions([]);
    setSpecClasses([]);
  }, []);

  // ── Start Edit ────────────────────────────────────────────
  const startEdit = useCallback(() => {
    if (!record) return;
    setMode('edit');
    setFormData({ ...record });
    setCustomerCodes(record.customer_codes || []);
    setCompositions(record.compositions || []);
    setSpecClasses(record.spec_classes || []);
  }, [record]);

  // ── Cancel (revert) ───────────────────────────────────────
  const cancel = useCallback(() => {
    setMode('browse');
    if (record) {
      setFormData({ ...record });
      setCustomerCodes(record.customer_codes || []);
      setCompositions(record.compositions || []);
      setSpecClasses(record.spec_classes || []);
    }
  }, [record]);

  // ── Save ──────────────────────────────────────────────────
  const save = useCallback(async () => {
    try {
      const payload = {
        master: formData as F113,
        customer_codes: customerCodes.filter(c => c.f1131_03.trim() || c.f1131_04.trim()),
        compositions: compositions.filter(c => c.f1132_11.trim()),
        spec_classes: specClasses.filter(c => c.f1133_11.trim()),
        garment_sizes: [],
        inspection_specs: [],
      };

      let result: F113;
      if (mode === 'add') {
        result = await p1113Api.create(payload);
        message.success('新增成功');
      } else {
        result = await p1113Api.update(record!.f113_01, record!.f113_02, payload);
        message.success('修改成功');
      }

      // Use response directly (T-F01)
      setRecord(result);
      setFormData({ ...result });
      setCustomerCodes(result.customer_codes || []);
      setCompositions(result.compositions || []);
      setSpecClasses(result.spec_classes || []);

      // Reload list and navigate to saved record
      const ids = await loadList();
      const savedPk1 = result.f113_01;
      const savedPk2 = result.f113_02;
      const idx = ids.findIndex(r => r.pk1 === savedPk1 && r.pk2 === savedPk2);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { errors?: Array<{ message: string }>; error?: string } } };
      const vErrs = axiosErr?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e) => message.error(e.message));
      } else {
        message.error(axiosErr?.response?.data?.error || '儲存失敗');
      }
    }
  }, [formData, customerCodes, compositions, specClasses, mode, record, loadList]);

  // ── Delete ────────────────────────────────────────────────
  const doDelete = useCallback(async () => {
    if (!record) return;
    try {
      await p1113Api.delete(record.f113_01, record.f113_02);
      message.success('刪除成功');
      const ids = await loadList();
      if (ids.length > 0) {
        setCurrentIdx(Math.min(currentIdx, ids.length - 1));
      } else {
        setCurrentIdx(-1);
        setRecord(null);
        setFormData({ ...defaultFormData });
      }
    } catch {
      message.error('刪除失敗');
    }
  }, [record, currentIdx, loadList]);

  // ── Quick Search ──────────────────────────────────────────
  const quickSearch = useCallback((pk1: string, pk2: string) => {
    if (isEditing) return;
    const key = pk1.trim();
    const key2 = pk2.trim();
    const idx = allIds.findIndex(r => {
      if (key2) return r.pk1 === key && r.pk2 === key2;
      return r.pk1 >= key;
    });
    if (idx >= 0) setCurrentIdx(idx);
  }, [isEditing, allIds]);

  return {
    mode, isEditing, loading, record, formData,
    allIds, currentIdx,
    customerCodes, setCustomerCodes,
    compositions, setCompositions,
    specClasses, setSpecClasses,
    getField, setField,
    navFirst, navPrev, navNext, navLast, navTo,
    startAdd, startEdit, cancel, save, doDelete, quickSearch,
  };
}
